import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import PremiumLock, { usePaid } from "../components/PremiumLock.jsx";

function ReviewDropdown({ reviews, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = "review-listbox";

  const selected = reviews.find((r) => String(r.id) === String(value));

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function openList() {
    const selectedIndex = reviews.findIndex((r) => String(r.id) === String(value));
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function closeList() {
    setOpen(false);
    setFocusedIndex(-1);
  }

  function select(id) {
    onChange(String(id));
    closeList();
    triggerRef.current?.focus();
  }

  function selectFocused() {
    if (focusedIndex >= 0 && focusedIndex < reviews.length) {
      select(reviews[focusedIndex].id);
    }
  }

  useEffect(() => {
    if (!open || focusedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[role='option']");
    if (items[focusedIndex]) {
      items[focusedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [open, focusedIndex]);

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        openList();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, reviews.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusedIndex(reviews.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        selectFocused();
        break;
      case "Tab":
        closeList();
        break;
      case "Escape":
        e.preventDefault();
        closeList();
        triggerRef.current?.focus();
        break;
    }
  }

  const activedescendant =
    open && focusedIndex >= 0 && reviews[focusedIndex]
      ? `review-option-${reviews[focusedIndex].id}`
      : undefined;

  return (
    <div
      className={"review-dropdown" + (open ? " review-dropdown--open" : "")}
      ref={ref}
      style={{ flex: "1 1 240px", position: "relative" }}
    >
      <button
        type="button"
        ref={triggerRef}
        className="review-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activedescendant}
        onClick={() => (open ? closeList() : openList())}
        onKeyDown={handleKeyDown}
      >
        <span className="review-dropdown__label">
          {selected ? `Week of ${new Date(selected.weekOf).toLocaleDateString()}` : "Select a review"}
        </span>
        <svg
          className="review-dropdown__chevron"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2.5 5l4.5 4 4.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          id={listboxId}
          ref={listRef}
          className="review-dropdown__list"
          role="listbox"
          aria-label="Select a weekly review"
        >
          {reviews.map((r, i) => (
            <li
              key={r.id}
              id={`review-option-${r.id}`}
              role="option"
              aria-selected={String(r.id) === String(value)}
              className={
                "review-dropdown__option" +
                (String(r.id) === String(value) ? " review-dropdown__option--selected" : "") +
                (i === focusedIndex ? " review-dropdown__option--focused" : "")
              }
              onMouseDown={() => select(r.id)}
              onMouseEnter={() => setFocusedIndex(i)}
            >
              Week of {new Date(r.weekOf).toLocaleDateString()}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Share() {
  const isPaid = usePaid();
  const [links, setLinks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedReviewId, setSelectedReviewId] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLink, setNewLink] = useState(null);

  useEffect(() => {
    if (!isPaid) return;
    apiFetch("/share").then(setLinks).catch((e) => setError(e.message));
    apiFetch("/share/weekly-reviews")
      .then((rows) => {
        setReviews(rows);
        if (rows.length > 0) setSelectedReviewId(String(rows[0].id));
      })
      .catch((e) => setError(e.message));
  }, [isPaid]);

  async function createFloorLink() {
    setBusy(true);
    setError(null);
    setNewLink(null);
    try {
      const link = await apiFetch("/share", { method: "POST", body: { type: "floor" } });
      setNewLink(link);
      setLinks((prev) => [...prev, link]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function createWeeklyReviewLink() {
    if (!selectedReviewId) return;
    setBusy(true);
    setError(null);
    setNewLink(null);
    try {
      const link = await apiFetch("/share", {
        method: "POST",
        body: { type: "weekly_review", weeklyReviewId: Number(selectedReviewId) },
      });
      setNewLink(link);
      setLinks((prev) => [...prev, link]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id) {
    setBusy(true);
    setError(null);
    try {
      const updated = await apiFetch(`/share/${id}/revoke`, { method: "POST" });
      setLinks((prev) => prev.map((l) => (l.id === id ? updated : l)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        ACCOUNTABILITY
      </div>
      <h1 style={{ marginBottom: 32 }}>Share your floor or a weekly review</h1>

      {!isPaid && (
        <PremiumLock title="Read-only share links">
          Generate a revocable link to your floor so a mentor or accountability partner can see it
          without an account. Available on Conquest Premium -- coming soon.
        </PremiumLock>
      )}

      {isPaid && (
        <>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>Share your floor</div>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>
              Anyone with the link can see your current floor -- nothing else. Revoke it any time.
            </p>
            <button className="btn-secondary" disabled={busy} onClick={createFloorLink}>
              Create share link
            </button>
            {newLink && (
              <div style={{ marginTop: 16 }}>
                <input readOnly value={newLink.shareUrl} style={{ width: "100%" }} onFocus={(e) => e.target.select()} />
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>Share a weekly review</div>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>
              Pick a past weekly review and generate a read-only link to just that review.
            </p>
            {reviews.length === 0 && <p className="empty-state">No weekly reviews yet.</p>}
            {reviews.length > 0 && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <ReviewDropdown
                  reviews={reviews}
                  value={selectedReviewId}
                  onChange={setSelectedReviewId}
                />
                <button className="btn-secondary" disabled={busy} onClick={createWeeklyReviewLink}>
                  Create share link
                </button>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>Your links</div>
            {links.length === 0 && <p className="empty-state">No share links yet.</p>}
            {links.length > 0 && (
              <div className="hairline-list">
                {links.map((l) => (
                  <div key={l.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>
                      {l.type === "floor" ? "Floor" : "Weekly review"} · {new Date(l.createdAt).toLocaleDateString()}
                      {l.revoked && <span style={{ color: "var(--error)" }}> · revoked</span>}
                    </span>
                    {!l.revoked && (
                      <button className="btn-secondary" disabled={busy} onClick={() => revoke(l.id)}>
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {error && <p style={{ color: "var(--error)", marginTop: 16 }}>{error}</p>}
    </div>
  );
}
